import React, {type FC, useMemo} from 'react';
import {ShouldRenderVarsKeys} from '../../../components/ProductPage/customCssVars/shouldRenderVars';
import {useStylesParams} from '../../providers/stylesParamsProvider';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {useAllStyles} from '../../providers/allStyleProvider';
import type {ProvidedGlobalProps} from '../../providers/globalPropsProvider';

type ConditionalRenderProps = {
  by: Partial<ShouldRenderVarsKeys>;
  dataHook?: string;
} & Omit<React.HTMLProps<HTMLDivElement>, 'style'> &
  ProvidedGlobalProps;

export const ConditionalRender: FC<ConditionalRenderProps> = ({
  by: renderCondition,
  className,
  dataHook,
  children,
  ...props
}: ConditionalRenderProps) => {
  const stylesParams = useStylesParams();
  const {styleParamsForAllBreakpoints, shouldRenderVarsForAllBreakpoints} = useAllStyles();
  const {isEditor} = useEnvironment();

  const shouldRender = useMemo(() => {
    if (renderCondition in stylesParams) {
      return styleParamsForAllBreakpoints.some(
        (stylesParamsPerBreakpoint) => stylesParamsPerBreakpoint.booleans[renderCondition]
      );
    }

    return shouldRenderVarsForAllBreakpoints.some(
      (shouldRenderVarsPerBreakpoint) => shouldRenderVarsPerBreakpoint[renderCondition]
    );
  }, [renderCondition, stylesParams, styleParamsForAllBreakpoints, shouldRenderVarsForAllBreakpoints]);

  if (!isEditor && !shouldRender) {
    return null;
  }

  const cssVariableName = `--${renderCondition.replace(':', '-')}-display`;
  const style: React.CSSProperties = {
    display: `var(${cssVariableName}, inherit)`,
  };

  return (
    <div className={className} data-hook={dataHook} style={style} {...props}>
      {children}
    </div>
  );
};
